/**
 * This is a common/shared store for tools used by any Editor pages.
 */
import { CursorState } from '@editor/config/constants/cursor'
import { EDITOR_DEFAULT_LABEL_FONT_SIZE } from '@editor/config/styles'
import { PointArray } from '@editor/interfaces/attribute'
import { TransformTypes } from '@editor/interfaces/canvas'
import {
  deleteStateFromLocalStorage,
  loadStateFromLocalStorage,
  saveStateToLocalStorage,
} from '@editor/services/localStorage'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { uniq } from 'lodash'

export interface ToolUtilState {
  /**
   * Cursor state
   */
  cursor: CursorState

  /**
   * Cursor to be used when set to CursorState.DEFAULT.
   * Should only be used if the current default differs from tool's config default.
   * (eg: Grab subtool within any tool with Crosshair as default cursor)
   */
  defaultCursor?: CursorState

  /**
   * The current anchor position in the 3D space.
   * Only set during the working phase of a tool.
   */
  currentAnchorPosition?: PointArray

  /**
   * Element IDs of objects that are currently hovered.
   */
  hoveredElementId: string

  /**
   * Element IDs of objects that are currently selected.
   */
  selectedElementIds: string[]

  /**
   * The ID of the element currently focused.
   * This is different from hovered or selected, as it's specifically for camera behavior
   * where the item set here will be used by camera toolbar for extra button to re-focus on
   * that specific item.
   */
  focusedElementId: string

  /**
   * Element IDs that are currently checked.
   */
  checkedElementIds: string[]

  /**
   * Object IDs of objects that needs to be hidden.
   */
  hiddenElementIds: string[]

  /**
   * The ID of the element currently being edited.
   *
   * Only for tools that has an editing mode separate from simply being selected (like TextAnnotation).
   */
  editingElementId?: string

  /**
   * The ID of the element currently being adjusted.
   * This is used to indicate that an adjustment action (like moving vertices) is happening.
   *
   * The difference between this and `editingElementId` is that `editingElementId` is like a mode
   * whether the user is adjusting the element or not,
   * while `adjustingElementId` is for the actual action of adjustment in progress.
   *
   * This will be automatically cleared on mouse up.
   */
  adjustingElementId?: string

  /**
   * The ID of the element currently being worked on (e.g., during creation).
   * This is different from `editingElementId` which is for editing existing elements.
   */
  workingElementId?: string

  /**
   * Element IDs that have been edited but not yet saved.
   */
  editedElementIds: string[]

  /**
   * Element IDs that are currently being saved to the backend.
   */
  savingElementIds: string[]

  /**
   * Element IDs that are currently being deleted.
   */
  deletingElementIds: string[]

  /**
   * Unique key of the setting that is currently being adjusted.
   */
  currentAdjustingKey?: string

  /**
   * Temporary stroke color as users are choosing a color (while the color picker is open).
   * This is used for performance optimization to prevent unnecessary re-renders as
   * the color changes.
   */
  currentTextColor?: string

  /**
   * Temporary stroke color as users are choosing a color (while the color picker is open).
   * This is used for performance optimization to prevent unnecessary re-renders as
   * the color changes.
   */
  currentStrokeColor?: string

  /**
   * Temporary fill color as users are choosing a color (while the color picker is open).
   * This is used for performance optimization to prevent unnecessary re-renders as
   * the color changes.
   */
  currentFillColor?: string

  /**
   * Temporary label text color as users are choosing a color (while the color picker is open).
   * This is used for performance optimization to prevent unnecessary re-renders as
   * the color changes.
   */
  currentLabelTextColor?: string

  /**
   * Temporary label background color as users are choosing a color (while the color picker is open).
   * This is used for performance optimization to prevent unnecessary re-renders as
   * the color changes.
   */
  currentLabelBackgroundColor?: string

  /**
   * Flag to indicate if some dragging action is happening.
   */
  isDragging: boolean

  /**
   * Flag to indicate if a vertex is being adjusted.
   */
  isAdjustingVertex: boolean

  /**
   * Selected transform type
   */
  selectedTransform: TransformTypes

  /**
   * Font size multiplier for distance labels.
   */
  labelFontSize: number

  /**
   * Disable creating new elements.
   * Used in situations where a new element is supposed to be created (eg: tool is active)
   * but we want to prevent creation (eg: another action needs to take place first).
   */
  disableCreate: boolean

  /**
   * Hard-lock all tools in the toolbar regardless of any other conditions.
   * For tool-based operations, consider using `isJobRunning` instead.
   * Only use this when operations are not part of the final tool submission process.
   */
  disableTools: boolean

  /**
   * IDs of tools that are currently working on something in the background.
   * This is mostly for async jobs (ones that return jobToken by BE).
   */
  workingTools: string[]

  /**
   * IDs of tools that have successfully completed their operations.
   * Once added, will automatically be removed when the tool is re-activated.
   */
  successTools: string[]
}

const initialState: ToolUtilState = {
  cursor: CursorState.NONE,
  hoveredElementId: '',
  focusedElementId: '',
  selectedElementIds: [],
  hiddenElementIds: [],
  checkedElementIds: [],
  editedElementIds: [],
  savingElementIds: [],
  deletingElementIds: [],
  workingTools: [],
  successTools: [],
  isDragging: false,
  isAdjustingVertex: false,
  disableCreate: false,
  disableTools: false,
  selectedTransform: TransformTypes.TRANSLATE,
  labelFontSize: loadStateFromLocalStorage('editorlabelFontSize', EDITOR_DEFAULT_LABEL_FONT_SIZE),
}

export const toolUtilSlice = createSlice({
  name: 'toolUtil',
  initialState,
  reducers: {
    setCurrentAnchorPosition: (state, action: PayloadAction<PointArray | undefined>) => {
      state.currentAnchorPosition = action.payload
    },
    resetCurrentAnchor: (state) => {
      state.currentAnchorPosition = undefined
    },
    setIsDragging: (state, action: PayloadAction<boolean>) => {
      state.isDragging = action.payload
    },
    setIsAdjustingVertex: (state, action: PayloadAction<boolean>) => {
      state.isAdjustingVertex = action.payload
    },
    setSelectedTransform: (state, action: PayloadAction<TransformTypes>) => {
      state.selectedTransform = action.payload
    },
    setDisableCreate: (state, action: PayloadAction<boolean>) => {
      state.disableCreate = action.payload
    },
    setDisableTools: (state, action: PayloadAction<boolean>) => {
      state.disableTools = action.payload
    },
    setCursor: (state, action: PayloadAction<CursorState>) => {
      state.cursor = action.payload
    },
    setDefaultCursor: (state, action: PayloadAction<CursorState | undefined>) => {
      state.defaultCursor = action.payload
    },

    // Working Tools
    addWorkingTool: (state, action: PayloadAction<string>) => {
      if (!state.workingTools.includes(action.payload)) {
        state.workingTools.push(action.payload)
      }
    },
    removeWorkingTool: (state, action: PayloadAction<string>) => {
      state.workingTools = state.workingTools.filter((id) => id !== action.payload)
    },
    clearWorkingTools: (state) => {
      state.workingTools = []
    },
    addSuccessTool: (state, action: PayloadAction<string>) => {
      if (!state.successTools.includes(action.payload)) {
        state.successTools.push(action.payload)
      }
    },
    removeSuccessTool: (state, action: PayloadAction<string>) => {
      state.successTools = state.successTools.filter((id) => id !== action.payload)
    },
    clearSuccessTools: (state) => {
      state.successTools = []
    },

    /**
     * Element-related actions
     */
    // Hover
    setHoveredElementId: (state, action: PayloadAction<string>) => {
      state.hoveredElementId = action.payload
    },

    // Focus
    setFocusedElementId: (state, action: PayloadAction<string>) => {
      state.focusedElementId = action.payload
    },

    // Selection
    setSelectedElementIds: (state, action: PayloadAction<string[]>) => {
      state.selectedElementIds = action.payload

      // if the editing element is in the selected IDs, clear it
      if (state.editingElementId && state.selectedElementIds.includes(state.editingElementId)) {
        state.editingElementId = undefined
      }
    },
    toggleSelectedElementId: (state, { payload }: PayloadAction<string>) => {
      const index = state.selectedElementIds.indexOf(payload)
      if (index > -1) {
        state.selectedElementIds.splice(index, 1)

        // if the editing element is in the selected IDs, clear it
        if (state.editingElementId && state.selectedElementIds.includes(state.editingElementId)) {
          state.editingElementId = undefined
        }
      } else {
        state.selectedElementIds.push(payload)
      }
    },
    toggleSelectedElementIds: (
      state,
      { payload: { ids, selected } }: PayloadAction<{ ids: string[]; selected: boolean }>,
    ) => {
      state.selectedElementIds = selected
        ? uniq([...state.selectedElementIds, ...ids])
        : state.selectedElementIds.filter((id) => !ids.includes(id))

      // if the editing element is in the selected IDs, clear it
      if (state.editingElementId && state.selectedElementIds.includes(state.editingElementId)) {
        state.editingElementId = undefined
      }
    },

    // Hidden
    toggleHiddenElementIds: (
      state,
      { payload: { ids, invisible } }: PayloadAction<{ ids: string[]; invisible: boolean }>,
    ) => {
      state.hiddenElementIds = invisible
        ? uniq([...state.hiddenElementIds, ...ids])
        : state.hiddenElementIds.filter((id) => !ids.includes(id))
    },

    // Checked
    setCheckedElementIds: (state, action: PayloadAction<string[]>) => {
      state.checkedElementIds = action.payload
    },
    toggleCheckedElementId: (state, { payload }: PayloadAction<string>) => {
      const index = state.checkedElementIds.indexOf(payload)
      if (index > -1) {
        state.checkedElementIds.splice(index, 1)
      } else {
        state.checkedElementIds.push(payload)
      }
    },

    // Working
    setWorkingElementId: (state, action: PayloadAction<string | undefined>) => {
      state.workingElementId = action.payload
      state.selectedElementIds = [] // clear selection when starting to work on a new element
    },
    resetWorkingElementId: (state, action: PayloadAction<string>) => {
      if (state.workingElementId === action.payload) {
        state.workingElementId = undefined
      }
    },

    // Editing
    setEditingElementId: (state, action: PayloadAction<string | undefined>) => {
      state.editingElementId = action.payload
    },
    setEditSelectedElementId: (state, action: PayloadAction<string>) => {
      state.selectedElementIds = [action.payload]
      state.editingElementId = action.payload
    },
    resetEditingElementId: (state, action: PayloadAction<string>) => {
      if (state.editingElementId === action.payload) {
        state.editingElementId = undefined
      }
    },

    setEditedElementIds: (state, action: PayloadAction<string[]>) => {
      state.editedElementIds = action.payload
    },
    addEditedElementId: (state, { payload }: PayloadAction<string>) => {
      if (!state.editedElementIds.includes(payload)) {
        state.editedElementIds.push(payload)
      }
    },

    // Adjusting
    setAdjustingElementId: (state, action: PayloadAction<string | undefined>) => {
      state.adjustingElementId = action.payload
    },

    // Saving
    addSavingElementIds: (state, action: PayloadAction<string[]>) => {
      state.savingElementIds = uniq([...state.savingElementIds, ...action.payload])
    },
    removeSavingElementIds: (state, action: PayloadAction<string[]>) => {
      state.savingElementIds = state.savingElementIds.filter((id) => !action.payload.includes(id))
    },
    setSavingElementIds: (state, action: PayloadAction<string[]>) => {
      state.savingElementIds = action.payload
    },

    // Deleting
    setDeletingElementIds: (state, action: PayloadAction<string[]>) => {
      state.deletingElementIds = action.payload
    },
    addDeletingElementId: (state, action: PayloadAction<string>) => {
      if (!state.deletingElementIds.includes(action.payload)) {
        state.deletingElementIds.push(action.payload)
      }
    },
    removeDeletingElementId: (state, action: PayloadAction<string>) => {
      state.deletingElementIds = state.deletingElementIds.filter((id) => id !== action.payload)
    },

    /**
     * Temporary values management
     */
    setCurrentStrokeColor: (state, { payload }: PayloadAction<string | undefined>) => {
      state.currentStrokeColor = payload
    },
    setCurrentFillColor: (state, { payload }: PayloadAction<string | undefined>) => {
      state.currentFillColor = payload
    },
    setCurrentTextColor: (state, { payload }: PayloadAction<string | undefined>) => {
      state.currentTextColor = payload
    },
    setCurrentLabelTextColor: (state, { payload }: PayloadAction<string | undefined>) => {
      state.currentLabelTextColor = payload
    },
    setCurrentLabelBackgroundColor: (state, { payload }: PayloadAction<string | undefined>) => {
      state.currentLabelBackgroundColor = payload
    },
    setCurrentAdjustingKey: (state, { payload }: PayloadAction<string | undefined>) => {
      state.currentAdjustingKey = payload
    },

    /**
     * Reset all states related to the provided element ID.
     * This is useful when an element is deleted or reset.
     */
    resetElement: (state, action: PayloadAction<string>) => {
      if (state.editingElementId === action.payload) state.editingElementId = undefined
      if (state.hoveredElementId === action.payload) state.hoveredElementId = ''
      if (state.focusedElementId === action.payload) state.focusedElementId = ''
      state.selectedElementIds = state.selectedElementIds.filter((id) => id !== action.payload)
      state.hiddenElementIds = state.hiddenElementIds.filter((id) => id !== action.payload)
      state.checkedElementIds = state.checkedElementIds.filter((id) => id !== action.payload)
    },

    /**
     * Label size management
     */
    setLabelFontSize: (state, action: PayloadAction<number>) => {
      state.labelFontSize = action.payload
      saveStateToLocalStorage('editorlabelFontSize', state.labelFontSize)
    },
    resetLabelFontSize: (state) => {
      state.labelFontSize = EDITOR_DEFAULT_LABEL_FONT_SIZE
      deleteStateFromLocalStorage('editorlabelFontSize')
    },
  },
})

export const {
  setCursor,
  setDefaultCursor,
  setCurrentAnchorPosition,
  resetCurrentAnchor,
  setIsDragging,
  setIsAdjustingVertex,
  setSelectedTransform,
  setDisableCreate,
  setDisableTools,

  // Working Tools
  addWorkingTool,
  removeWorkingTool,
  clearWorkingTools,
  addSuccessTool,
  removeSuccessTool,
  clearSuccessTools,

  // Element actions
  setHoveredElementId,
  toggleHiddenElementIds,

  setFocusedElementId,

  setSelectedElementIds,
  toggleSelectedElementId,
  toggleSelectedElementIds,

  setCheckedElementIds,
  toggleCheckedElementId,

  setWorkingElementId,
  resetWorkingElementId,

  setEditingElementId,
  setEditSelectedElementId,
  resetEditingElementId,

  setAdjustingElementId,

  setEditedElementIds,
  addEditedElementId,

  addSavingElementIds,
  removeSavingElementIds,
  setSavingElementIds,

  setDeletingElementIds,
  addDeletingElementId,
  removeDeletingElementId,

  // Temporary values
  setCurrentStrokeColor,
  setCurrentFillColor,
  setCurrentTextColor,
  setCurrentLabelTextColor,
  setCurrentLabelBackgroundColor,
  setCurrentAdjustingKey,

  // Label size
  setLabelFontSize,
  resetLabelFontSize,

  // Resets
  resetElement,
} = toolUtilSlice.actions
export default toolUtilSlice.reducer
